// Public check-in page (QR code landing) — handles two kinds:
//   • kind=presence    → legacy session-based ticket
//   • kind=inscription → new Fêtes 2027 paid inscription (passes, meals, …)
// Hitting this page registers presence (idempotent) and shows what the
// participant has paid for so the host at the welcome desk can validate.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";

const AGE_LABELS = {
  under_6: { fr: "Moins de 6 ans", en: "Under 6" },
  "6_12":   { fr: "6 – 12 ans",     en: "6 – 12" },
  "13_17":  { fr: "13 – 17 ans",    en: "13 – 17" },
  adult:    { fr: "Adulte",         en: "Adult" },
  senior:   { fr: "65 ans et +",    en: "65+" },
};

export default function CheckIn() {
  const { token } = useParams();
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    api.get(`/presence/checkin/${token}`, { signal: controller.signal })
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        setError(e?.response?.data?.detail || "Erreur");
      });
    return () => controller.abort();
  }, [token]);

  return (
    <div data-testid="checkin-page" className="min-h-screen flex items-center justify-center py-16"
         style={{ background: "var(--parchment)" }}>
      <div className="container-prieure max-w-xl">
        <div className="illuminated-border bg-white text-center py-12 px-8">
          <Ornament className="mb-6" />
          {error ? (
            <>
              <div className="font-display text-5xl mb-3" style={{ color: "var(--cinnabar)" }}>✕</div>
              <h1 className="font-display text-3xl mb-3">{isFr ? "Lien invalide" : "Invalid link"}</h1>
              <p className="font-body" style={{ color: "var(--ink-2)" }}>{error}</p>
            </>
          ) : !data ? (
            <p className="font-body" style={{ color: "var(--muted)" }}>
              {isFr ? "Pointage en cours…" : "Checking you in…"}
            </p>
          ) : data.kind === "inscription" ? (
            <InscriptionTicket data={data} isFr={isFr} />
          ) : (
            <PresenceTicket data={data} isFr={isFr} />
          )}
          {data && (
            <div className="mt-10">
              <Link to="/" className="btn btn-outline">
                {isFr ? "Retour à l'accueil" : "Back to home"}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PresenceTicket({ data, isFr }) {
  return (
    <>
      <div className="font-display text-6xl mb-4" style={{ color: data.already_present ? "var(--gold)" : "var(--moss)" }}>
        {data.already_present ? "✓✓" : "✓"}
      </div>
      <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--cinnabar)" }}>
        PRESENCE — Fêtes de saint Léonard
      </div>
      <h1 className="font-display text-4xl mb-1">{data.name}</h1>
      <p className="font-display italic text-xl mb-6" style={{ color: "var(--ink-2)" }}>
        {data.already_present
          ? (isFr ? "Vous étiez déjà pointé — bienvenue !" : "Already checked in — welcome back!")
          : (isFr ? "Présence enregistrée. Bienvenue !" : "Presence recorded. Welcome!")}
      </p>
      <div className="space-y-1 font-body" style={{ color: "var(--ink-2)" }}>
        <p><strong>{data.formula}</strong> × {data.quantity}</p>
        {data.day_label && <p>{data.day_label}</p>}
        {data.workshop && <p>{isFr ? "Atelier :" : "Workshop:"} {data.workshop}</p>}
        {!data.paid && (
          <p className="font-caps text-[0.7rem] mt-4" style={{ color: "var(--cinnabar)" }}>
            {isFr ? "⚠ Paiement non confirmé — à régulariser à l'accueil" : "⚠ Payment unconfirmed — please settle at the welcome desk"}
          </p>
        )}
      </div>
    </>
  );
}

function InscriptionTicket({ data, isFr }) {
  return (
    <>
      <div className="font-display text-6xl mb-4" style={{ color: data.already_present ? "var(--gold)" : "var(--moss)" }}>
        {data.already_present ? "✓✓" : "✓"}
      </div>
      <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--cinnabar)" }}>
        BILLET — Fêtes de saint Léonard 2027
      </div>
      <h1 className="font-display text-4xl mb-1">{data.name}</h1>
      {data.city && <p className="font-body text-sm" style={{ color: "var(--muted)" }}>{data.city}</p>}
      <p className="font-display italic text-xl mt-4 mb-6" style={{ color: "var(--ink-2)" }}>
        {data.already_present
          ? (isFr ? "Déjà pointé — bienvenue à nouveau !" : "Already checked in — welcome back!")
          : (isFr ? "Présence enregistrée. Bienvenue !" : "Presence recorded. Welcome!")}
      </p>

      {data.people && data.people.length > 0 && (
        <div className="text-left mb-6 p-4" style={{ background: "var(--parchment-2)" }}>
          <div className="font-caps text-[0.6rem] mb-3" style={{ color: "var(--cinnabar)", letterSpacing: "0.22em" }}>
            ◆ {isFr ? "PARTICIPANTS" : "PARTICIPANTS"}
          </div>
          <ul className="space-y-1 font-body text-sm">
            {data.people.map((p, i) => (
              <li key={i} data-testid={`checkin-person-${i}`}
                  className="flex justify-between gap-3" style={{ color: "var(--ink-2)" }}>
                <span><strong>{p.first_name} {p.last_name}</strong></span>
                <span className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                  {(AGE_LABELS[p.age_bucket] || {})[isFr ? "fr" : "en"] || p.age_bucket}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.lines && data.lines.length > 0 && (
        <div className="text-left mb-6 p-4" style={{ background: "var(--parchment-2)" }}>
          <div className="font-caps text-[0.6rem] mb-3" style={{ color: "var(--cinnabar)", letterSpacing: "0.22em" }}>
            ◆ {isFr ? "PRESTATIONS RÉSERVÉES" : "RESERVED ITEMS"}
          </div>
          <ul className="space-y-2 font-body text-sm">
            {data.lines.map((ln, i) => {
              const lbl = isFr
                ? ((ln.label || {}).fr || (ln.label || {}).en)
                : ((ln.label || {}).en || (ln.label || {}).fr);
              return (
                <li key={i} data-testid={`checkin-line-${i}`}
                    className="flex justify-between items-center gap-3" style={{ color: "var(--ink-2)" }}>
                  <span><strong>{lbl}</strong></span>
                  <span className="font-display">×{ln.quantity} · {Number(ln.subtotal_eur || 0).toFixed(2)} €</span>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-between items-baseline pt-3 mt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <span className="font-caps" style={{ letterSpacing: "0.22em" }}>
              {isFr ? "TOTAL RÉGLÉ" : "TOTAL PAID"}
            </span>
            <span data-testid="checkin-total" className="font-display text-2xl" style={{ color: "var(--moss)" }}>
              {Number(data.total || 0).toFixed(2)} €
            </span>
          </div>
        </div>
      )}

      {!data.paid && data.payment_status !== "free" && (
        <p className="font-caps text-[0.7rem]" style={{ color: "var(--cinnabar)" }}>
          {isFr ? "⚠ Paiement non confirmé — à régulariser à l'accueil" : "⚠ Payment unconfirmed — please settle at the welcome desk"}
        </p>
      )}
    </>
  );
}
